import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CrewAppIssueReporter } from "@/components/admin/CrewAppIssueReporter";

type Props = {
  children: ReactNode;
  label?: string;
};

type State = {
  error: Error | null;
};

export class AdminSectionErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Admin section failed", { label: this.props.label, error, info });
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-destructive mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold">This section hit an app error.</p>
              <p className="text-sm text-muted-foreground">
                The rest of the app is still available. Try again, or send this screen to the office if it keeps happening.
              </p>
              {this.state.error.message && (
                <p className="mt-2 rounded-md bg-background/70 px-2 py-1 text-xs text-muted-foreground">
                  {this.state.error.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => this.setState({ error: null })}>
              <RefreshCcw className="h-4 w-4 mr-2" />
              Try again
            </Button>
            <CrewAppIssueReporter errorMessage={this.state.error.message} />
          </div>
        </CardContent>
      </Card>
    );
  }
}
